import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Menu, Tabs } from '@mui/material';
import { noteTabs } from 'spw-components/worksheet/constants';
import Accordion from 'spw-programming-projects/program-summary/program-summary-tabs/expansion-panel';
import { ICategoryNote, Scenario } from 'spw-redux/modules/calculators/types';
import { Row } from 'spw-shared/globalComponents/grid';
import TabPanel from 'spw-shared/globalComponents/tabPanel';
import { colors } from 'spw-theme';
import { ColorDot, SmallColorDot } from 'spw-components/worksheet/styles';
import {
  MarginLeft8,
  MarginTop4,
} from 'spw-shared/globalComponents/containers';
import {
  DEFAULT_STREET_ADDRESS,
  GENERAL_CATEGORY_TITLE,
  NOTE_GENERAL_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SHARED_SPACES_KEY,
  TITLES,
} from 'spw-constants';
import {
  NoteContent,
  NotesCategoryName,
  NotesContainer,
  NotesContent,
  NotesDepartmentName,
  NoteSecondaryIdentifier,
  NotesListTitleContainer,
  NotesMenuItem,
  NotesScenarioName,
  NotesTab,
  NotesTitle,
  NoteTabsContainer,
  NoteText,
} from 'spw-programming-projects/program-summary/program-summary-tabs/styles';
import { groupBy } from 'ramda';
import { camelCase, snakeCase } from 'lodash';
import { ExportButtonContainer } from 'spw-programming-projects/program-summary/components/styles';
import { SPWExportButton } from 'spw-shared/globalComponents/buttons';
import {
  isDepartmentScenario,
  isFloorScenario,
  isMacro,
} from 'spw-components/worksheet/utils';
import {
  IBuilding,
  IDepartment,
  IFloor,
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  getColor,
  getZipNamePrefix,
  Keys,
  sanitizeFileName,
} from 'spw-shared/utils';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import { getWorksheetsByScenarioIdMap } from 'spw-components/programming-scenarios/utils';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { prepareAndExecuteReportDownload } from 'spw-redux/modules/downloads/actions';
import {
  selectIndividualReportZipName,
  selectIsDownloadingIndividualReport,
} from 'spw-redux/modules/downloads/selectors';
import { PNG } from 'spw-redux/modules/reports/types';
import { REPORT_TYPES } from 'spw-redux/modules/reports/constants';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { PlainProgramScenario } from '../../../../models/Scenario';
import { PlainWorksheet } from '../../../../models/Worksheet';

interface SummaryNotesProps {
  scenario: PlainProgramScenario;
  worksheet: PlainWorksheet;
  projectColors: IProjectColors;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  building: IBuilding;
  floor?: IFloor;
  tpsWorksheets?: PlainWorksheet[];
}

type NotesReportType = 'notesByCategory' | 'allNotes';
const getNoteCategories = (
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[],
  detailedSharedConfig: boolean
) => {
  let categories = [
    { name: GENERAL_CATEGORY_TITLE, key: NOTE_GENERAL_CATEGORY },
  ];

  const spaceTypeCategories = detailedSharedConfig
    ? projectSpaceTypeCategories
    : projectSpaceTypeCategories.filter(
        (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
          isIWCategoryOrSubcategory(projectSpaceTypeCategory)
      );

  categories = categories.concat(
    spaceTypeCategories.map(
      (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => ({
        name: projectSpaceTypeCategory.name,
        key: projectSpaceTypeCategory.defaultSpaceTypeCategory.category,
      })
    )
  );

  if (!detailedSharedConfig) {
    categories.push({
      name: TITLES.SHARED_SPACES,
      key: SHARED_SPACES_CATEGORY,
    });
  }

  return categories;
};

const NotesListTitle = ({
  name,
  scenario,
  department,
  floor,
  testingPrefix,
}: {
  name: string;
  scenario: Scenario | PlainProgramScenario;
  department?: IDepartment;
  floor?: IFloor;
  testingPrefix: string;
}) => {
  let title: string;

  if (isTotalProgram(scenario)) {
    title = 'MULTIPLE DEPARTMENTS';
  } else if (isDepartmentScenario(scenario)) {
    title = department?.name;
  } else if (!isMacro(scenario)) {
    title =
      floor && isFloorScenario(scenario?.calculatorType)
        ? floor.name
        : 'Building';
  }

  return (
    <NotesTitle data-testid={`${testingPrefix}_notesTitle`}>
      {name.toUpperCase()} {title ? `- ${title.toUpperCase()}` : ''}
      {isDepartmentScenario(scenario) && (
        <NoteSecondaryIdentifier
          data-testid={`${testingPrefix}_secondaryIdentifier`}
        >
          {` ${department?.secondaryIdentifier?.toUpperCase() || ''}`}
        </NoteSecondaryIdentifier>
      )}
    </NotesTitle>
  );
};

const NotesList = ({
  notesByCategory,
  projectColors,
  category,
  isDownload,
  testingPrefix,
}: {
  notesByCategory: { [key: string]: any };
  projectColors: IProjectColors;
  category: string;
  isDownload?: boolean;
  testingPrefix: string;
}) => {
  const notes = notesByCategory[
    snakeCase(category).toUpperCase()
  ]?.filter((categoryNote: ICategoryNote) => categoryNote.note?.trim());

  if (!notes) return null;

  return notes?.map((worksheetNote: ICategoryNote) => {
    let color = '';
    if (category.toLowerCase() === 'general') {
      color = colors.offWhite;
    } else {
      color = getColor(camelCase(category) as Keys, projectColors);
    }

    const lines = worksheetNote?.note?.split('\n');

    return lines.map((line: string, index: number) => {
      return line?.trim() ? (
        <NoteContent
          display="flex"
          key={`note-${Math.random().toString(36).substr(2, 9)}-${
            worksheetNote.id
          }`}
          className={`${isDownload ? 'worksheet-notes' : ''}`}
          style={{
            marginBottom: index === lines.length - 1 && !isDownload ? 13 : 0,
          }}
        >
          <MarginTop4>
            <SmallColorDot color={color} />
          </MarginTop4>
          <MarginLeft8>
            <NoteText
              className={isDownload ? 'worksheet-note' : ''}
              data-testid={`${testingPrefix}_${worksheetNote.id}_${index}`}
            >
              {line}
            </NoteText>
          </MarginLeft8>
        </NoteContent>
      ) : null;
    });

    return null;
  });
};
export const WorksheetNote = ({
  notesByCategory,
  category,
  scenario,
  department,
  projectColors,
  floor,
  isDownload = false,
  isRenderingForExport,
  options = {
    showCategoryName: false,
    showDepartmentName: false,
    showBorder: false,
  },
}: {
  notesByCategory: { [key: string]: ICategoryNote[] };
  category: {
    name: string;
    key: string;
  };
  scenario: PlainProgramScenario;
  department?: IDepartment;
  projectColors: IProjectColors;
  floor?: IFloor;
  isDownload?: boolean;
  isRenderingForExport?: boolean;
  options?: {
    showCategoryName?: boolean;
    showDepartmentName?: boolean;
    showBorder?: boolean;
  };
}) => {
  const notes = notesByCategory[
    snakeCase(category.key).toUpperCase()
  ]?.filter((categoryNote: ICategoryNote) => categoryNote.note?.trim());

  const testingPrefix = 'programming_ps_notes';

  if (window.SPWExport || isRenderingForExport || isDownload) {
    const { showCategoryName, showBorder, showDepartmentName } = options;

    if (!notes?.length && showCategoryName)
      return (
        <NotesCategoryName>
          {`${category.name.replace(/_/g, ' ')} ${
            category.key !== NOTE_GENERAL_CATEGORY ? 'NOTES' : ''
          }`.trim()}
        </NotesCategoryName>
      );

    return (
      <NotesContent
        className={`${isDownload ? 'worksheet-notes' : 'm-4'} ${
          showBorder ? 'show-border' : ''
        }`}
        key={`notes-content-${category.key}-${scenario.id}`}
      >
        {isDownload ? (
          <>
            {showCategoryName && (
              <>
                <NotesCategoryName>
                  {`${category.name.replace(/_/g, ' ')} ${
                    category.key !== NOTE_GENERAL_CATEGORY ? 'NOTES' : ''
                  }`.trim()}
                </NotesCategoryName>
                {isTotalProgram(scenario) && notes?.length > 0 && (
                  <NotesScenarioName marginLeft={10}>
                    TOTAL PROGRAM
                  </NotesScenarioName>
                )}
              </>
            )}
            {showDepartmentName &&
              notes?.length > 0 &&
              isDepartmentScenario(scenario) && (
                <NotesTitle>
                  <NotesDepartmentName>
                    {department?.name} {department?.secondaryIdentifier || ''}
                  </NotesDepartmentName>
                </NotesTitle>
              )}
          </>
        ) : (
          <NotesListTitleContainer className={!notes?.length ? 'empty' : ''}>
            <NotesListTitle
              name={category.name}
              scenario={scenario}
              department={department}
              floor={floor}
              testingPrefix=""
            />
            <NotesScenarioName>{scenario?.name}</NotesScenarioName>
          </NotesListTitleContainer>
        )}
        <NotesList
          notesByCategory={notesByCategory}
          projectColors={projectColors}
          category={category.key}
          isDownload={isDownload}
          testingPrefix=""
        />
      </NotesContent>
    );
  }

  return (
    <NotesContent key={`notes-content-${category.key}-${scenario.id}`}>
      <NotesListTitleContainer className={!notes?.length ? 'empty' : ''}>
        <NotesListTitle
          name={category.name}
          scenario={scenario}
          department={department}
          floor={floor}
          testingPrefix={`${testingPrefix}_${category.key}`}
        />
        <NotesScenarioName
          data-testid={`${testingPrefix}_${category.key}_scenarioName`}
        >
          {scenario?.name}
        </NotesScenarioName>
      </NotesListTitleContainer>
      <NotesList
        notesByCategory={notesByCategory}
        projectColors={projectColors}
        category={category.key}
        testingPrefix={`${testingPrefix}_${category.key}`}
      />
    </NotesContent>
  );
};

export const TotalProgramWorksheetNotes = ({
  totalProgram,
  category,
  scenarioWorksheets,
  projectColors,
  isRenderingForExport,
  isDownload,
}: {
  totalProgram: PlainProgramScenario;
  category: {
    name: string;
    key: string;
  };
  scenarioWorksheets: { [key: string]: PlainWorksheet };
  projectColors: IProjectColors;
  isRenderingForExport?: boolean;
  isDownload?: boolean;
}) => {
  const uppercaseCategoryKey = snakeCase(category.key).toUpperCase();

  const totalProgramScenarios = totalProgram.linkedScenarios.sort(
    (scenarioA, scenarioB) => scenarioA.index - scenarioB.index
  );

  const totalProgramNotes =
    totalProgram.notes?.filter((note: ICategoryNote) => note.note?.trim()) ||
    [];

  const numberOfNotes =
    totalProgramNotes.length +
    totalProgramScenarios.reduce((sum, scenario) => {
      const scenarioWorksheet = scenarioWorksheets[scenario.id];
      const notes =
        scenarioWorksheet?.notes?.filter((note: ICategoryNote) => note.note) ||
        [];
      return sum + notes.length;
    }, 0);

  if (isDownload && !numberOfNotes) {
    return null;
  }

  const totalProgramNotesByCategory = groupBy(
    (note: ICategoryNote) => note.category
  )(totalProgramNotes);

  const doLinkedScenariosHaveNotes = totalProgramScenarios.some((scenario) => {
    const scenarioWorksheet = scenarioWorksheets[scenario.id];
    return scenarioWorksheet?.notes?.find(
      (note) => note.note?.trim() && note.category === uppercaseCategoryKey
    );
  });

  const totalProgramScenariosWithNotes = totalProgramScenarios
    .map((scenario: PlainProgramScenario) => {
      const scenarioWorksheet = scenarioWorksheets[scenario.id];
      return {
        scenario,
        notes:
          scenarioWorksheet?.notes?.filter((note: ICategoryNote) =>
            note.note?.trim()
          ) || [],
      };
    })
    .filter(({ notes }) => {
      if (!notes.length) return false;
      const notesByCategory: { [key: string]: any } = groupBy(
        (note: ICategoryNote) => note.category
      )(notes);

      return notesByCategory[uppercaseCategoryKey]?.length > 0;
    });

  return (
    <div>
      {(totalProgramNotesByCategory[uppercaseCategoryKey]?.length > 0 ||
        doLinkedScenariosHaveNotes) && (
        <WorksheetNote
          notesByCategory={totalProgramNotesByCategory}
          category={category}
          scenario={totalProgram}
          projectColors={projectColors}
          isDownload={isDownload}
          isRenderingForExport={isRenderingForExport}
          options={
            isDownload
              ? {
                  showCategoryName: true,
                  showBorder: !doLinkedScenariosHaveNotes,
                }
              : {}
          }
        />
      )}
      {totalProgramScenariosWithNotes.map(
        ({ scenario, notes }, index: number) => {
          const notesByCategory: { [key: string]: any } = groupBy(
            (note: ICategoryNote) => note.category
          )(notes);

          return (
            <WorksheetNote
              key={`worksheet-note-${category.key
                .toLowerCase()
                .replace(/\s/g, '-')}-${scenario.id}`}
              notesByCategory={notesByCategory}
              category={category}
              scenario={scenario}
              department={scenario.department}
              projectColors={projectColors}
              isRenderingForExport={isRenderingForExport}
              isDownload={isDownload}
              options={
                isDownload
                  ? {
                      showCategoryName: false,
                      showDepartmentName: true,
                      showBorder:
                        index === totalProgramScenariosWithNotes.length - 1,
                    }
                  : {}
              }
            />
          );
        }
      )}
    </div>
  );
};

const TotalProgramNotes = ({
  value,
  scenario,
  projectSpaceTypeCategories,
  tpsWorksheets,
  projectColors,
}: {
  value: number;
  scenario: PlainProgramScenario;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  tpsWorksheets: PlainWorksheet[];
  projectColors: IProjectColors;
}) => {
  const scenarioWorksheets: {
    [key: string]: PlainWorksheet;
  } = getWorksheetsByScenarioIdMap(tpsWorksheets);

  const categories = getNoteCategories(
    projectSpaceTypeCategories,
    !scenario.isShared
  );

  return (
    <NotesContainer>
      {categories.map((category, index) => {
        return (
          <TabPanel
            value={value}
            index={index}
            key={`tab-panel-${category.key.toLowerCase().replace(/\s/, '-')}`}
          >
            <TotalProgramWorksheetNotes
              key={`total-program-notes-${category.key
                .toLowerCase()
                .replace(/\s/, '-')}`}
              totalProgram={scenario}
              category={category}
              scenarioWorksheets={scenarioWorksheets}
              projectColors={projectColors}
            />
          </TabPanel>
        );
      })}
    </NotesContainer>
  );
};

const WorksheetNotes = ({
  worksheet,
  scenario,
  projectSpaceTypeCategories,
  value,
  department,
  projectColors,
  floor,
}: {
  worksheet: PlainWorksheet;
  scenario: PlainProgramScenario;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  value: number;
  department?: IDepartment;
  projectColors: IProjectColors;
  floor?: IFloor;
}) => {
  if (!scenario || !worksheet || !worksheet.notes) {
    return null;
  }

  const notesByCategory: { [key: string]: any } = groupBy(
    (note: ICategoryNote) => note.category
  )(worksheet.notes);

  const categories = getNoteCategories(
    projectSpaceTypeCategories,
    !scenario.isShared
  );

  return (
    <NotesContainer>
      {categories.map((category, index) => {
        return (
          <TabPanel
            value={value}
            index={index}
            key={`tab-panel-${category.key}`}
          >
            <WorksheetNote
              key={`worksheet-note-${category.key}`}
              notesByCategory={notesByCategory}
              category={category}
              scenario={scenario}
              department={department}
              projectColors={projectColors}
              floor={floor}
            />
          </TabPanel>
        );
      })}
    </NotesContainer>
  );
};

export const SummaryNotes: React.FC<SummaryNotesProps> = ({
  scenario,
  worksheet,
  projectColors,
  projectSpaceTypeCategories,
  building,
  floor,
  tpsWorksheets,
}: SummaryNotesProps) => {
  const componentRef = useRef();

  if (!scenario || !worksheet) {
    return null;
  }

  const dispatch = useDispatch();
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const isDownloadingIndividualReport = useSelector(
    selectIsDownloadingIndividualReport
  );
  const individualReportZipName = useSelector(selectIndividualReportZipName);

  const [notesTab, setNotesTab] = useState(noteTabs.INDIVIDUAL_WORKSPACES);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleChangeTab = (event: React.ChangeEvent, newTab: number) => {
    setNotesTab(newTab);
  };

  const isTotalProgramScenario = isTotalProgram(scenario);

  const handleExportOnClick = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const spaceTypeCategories = !scenario.isShared
    ? projectSpaceTypeCategories
    : projectSpaceTypeCategories.filter(
        (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
          isIWCategoryOrSubcategory(projectSpaceTypeCategory)
      );

  // TODO: Move to the backend
  const zipName = useMemo(() => {
    const floorName = scenario.floorId && floor ? floor.name : '';
    return `${
      building?.streetAddress &&
      building.streetAddress !== DEFAULT_STREET_ADDRESS
        ? `${building.streetAddress}_`
        : ''
    }${scenario.name}_`
      .concat(
        isMacro(scenario) ? 'Macro_' : getZipNamePrefix(scenario, floorName)
      )
      .concat('Notes.zip');
  }, [building, scenario, floor]);

  const handleNotesDownload = useCallback(
    (reportType: NotesReportType, category?: string) => {
      dispatch(
        prepareAndExecuteReportDownload(
          {
            reportType,
            category,
            entityId: scenario.id,
            format: PNG,
          },
          zipName
        )
      );
      setAnchorEl(null);
    },
    [zipName, scenario, dispatch]
  );

  const testingPrefix = 'programming_ps_notes';

  return (
    <div data-testid={`${testingPrefix}_accordion_row`}>
      <Accordion title="Program Worksheet Notes">
        <NoteTabsContainer>
          <Row>
            <Tabs value={notesTab} onChange={handleChangeTab}>
              <NotesTab
                key={`tab-${NOTE_GENERAL_CATEGORY}`}
                label={
                  <Box display="flex" alignItems="center">
                    <ColorDot color={colors.offWhite} className="colorDot" />
                    <MarginLeft8>
                      <Box>{GENERAL_CATEGORY_TITLE}</Box>
                    </MarginLeft8>
                  </Box>
                }
                data-testid={`${testingPrefix}_tab_${NOTE_GENERAL_CATEGORY}`}
              />
              {spaceTypeCategories.map(
                (category: IProjectSpaceTypeCategory) => {
                  return (
                    <NotesTab
                      key={`tab-${category.defaultSpaceTypeCategory.category}`}
                      label={
                        <Box display="flex" alignItems="center">
                          <ColorDot
                            color={getColor(
                              category.defaultSpaceTypeCategory.category,
                              projectColors
                            )}
                          />
                          <MarginLeft8>
                            <Box>{category.name}</Box>
                          </MarginLeft8>
                        </Box>
                      }
                      data-testid={`${testingPrefix}_tab_${category.defaultSpaceTypeCategory.category}`}
                    />
                  );
                }
              )}
              {scenario.isShared && (
                <NotesTab
                  key={`tab-${SHARED_SPACES_KEY}`}
                  label={
                    <Box display="flex" alignItems="center">
                      <ColorDot
                        color={getColor(SHARED_SPACES_KEY, projectColors)}
                      />
                      <MarginLeft8>{TITLES.SHARED_SPACES}</MarginLeft8>
                    </Box>
                  }
                  data-testid={`${testingPrefix}_tab_${SHARED_SPACES_KEY}`}
                />
              )}
            </Tabs>
            <Box flexGrow={1}>
              <ExportButtonContainer>
                <SPWExportButton
                  disabled={currentUserIsViewOnly}
                  busy={
                    isDownloadingIndividualReport &&
                    sanitizeFileName(zipName) === individualReportZipName
                  }
                  onClick={
                    isDownloadingIndividualReport
                      ? () => Function.prototype
                      : handleExportOnClick
                  }
                />
                <Menu
                  id="download-worksheet-notes"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  disableScrollLock
                >
                  <NotesMenuItem key="header" className="title" disabled>
                    DOWNLOAD OPTIONS
                  </NotesMenuItem>
                  <NotesMenuItem
                    key="item-all"
                    onClick={() =>
                      handleNotesDownload(
                        REPORT_TYPES.ALL_NOTES as NotesReportType
                      )
                    }
                  >
                    All Space Type Categories
                  </NotesMenuItem>
                  <NotesMenuItem
                    key={`item-${NOTE_GENERAL_CATEGORY}`}
                    onClick={() =>
                      handleNotesDownload(
                        REPORT_TYPES.NOTES_BY_CATEGORY as NotesReportType,
                        NOTE_GENERAL_CATEGORY
                      )
                    }
                  >
                    {GENERAL_CATEGORY_TITLE}
                  </NotesMenuItem>

                  {spaceTypeCategories.map(
                    (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
                      return (
                        <NotesMenuItem
                          key={`item-${projectSpaceTypeCategory.defaultSpaceTypeCategory.category}`}
                          onClick={() =>
                            handleNotesDownload(
                              REPORT_TYPES.NOTES_BY_CATEGORY as NotesReportType,
                              projectSpaceTypeCategory.defaultSpaceTypeCategory
                                .category
                            )
                          }
                        >
                          {projectSpaceTypeCategory.name}
                        </NotesMenuItem>
                      );
                    }
                  )}
                  {scenario.isShared && (
                    <NotesMenuItem
                      key={`item-${SHARED_SPACES_CATEGORY}`}
                      onClick={() =>
                        handleNotesDownload(
                          REPORT_TYPES.NOTES_BY_CATEGORY as NotesReportType,
                          SHARED_SPACES_CATEGORY
                        )
                      }
                    >
                      {TITLES.SHARED_SPACES}
                    </NotesMenuItem>
                  )}
                </Menu>
              </ExportButtonContainer>
            </Box>
          </Row>
          <Row>
            <div ref={componentRef}>
              {isTotalProgramScenario ? (
                <TotalProgramNotes
                  value={notesTab}
                  scenario={scenario}
                  projectSpaceTypeCategories={projectSpaceTypeCategories}
                  tpsWorksheets={tpsWorksheets}
                  projectColors={projectColors}
                />
              ) : (
                <WorksheetNotes
                  worksheet={worksheet}
                  scenario={scenario}
                  projectSpaceTypeCategories={projectSpaceTypeCategories}
                  value={notesTab}
                  department={scenario.department}
                  projectColors={projectColors}
                  floor={floor}
                />
              )}
            </div>
          </Row>
        </NoteTabsContainer>
      </Accordion>
    </div>
  );
};
